import ButtonTextLoader from 'components/Loaders/ButtonTextLoader';
import { TaskStatusOptions } from 'modules/constants';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonIcon, WhiteButtonWithIcon } from 'styledComponents/buttons';
import { exportToExcel } from 'utils/exportToExcel';

import excelIcon from '../../../assets/img/icons/excel-green.svg';
import BottomPagination from '../../../components/Pagination/BottomPagination/BottomPagination';
import TopPagination from '../../../components/Pagination/TopPagination/TopPagination';
import {
  CommonFlex,
  CommonGrid,
  HeaderContainer,
  PageTitle,
} from '../../../styledComponents/common';
import DeleteTaskModal from '../DeleteTaskModal';
import SubTasksList from './SubTasksList';
import SubTaskFilter from './subTaskFilter';

const SubTasks = ({
  selectedOrganization,
  getOrganizationMembersList,
  orgMembersList,
  loadingTaskAssigneeId,
  isLoading,
  officesShortList,
  getOfficesShortList,
  getSubTasks,
  subTasksList,
  deleteTask,
  deletedCallback,
  deleteIsLoading,
  createdTaskInfo,
  createTask,
  // pagination
  totalTasksCount,
  pageSize,
  history,
  locationsList,
  getTaskLocation,
  getPublicZoneList,
  publicZoneList,
  defaultSubTasks,
  getDefaultSubTasks,
}) => {
  const { t } = useTranslation();
  const urlParams = new URLSearchParams(window.location.search);
  const statusFromParams = urlParams.get('status');
  const zoneFromParams = urlParams.get('zone');
  if (statusFromParams || zoneFromParams) {
    const newUrl = `${window.location.pathname}`;
    window.history.replaceState({}, '', newUrl);
  }

  const [selectedDate, setSelectedDate] = useState({
    startDate:
      statusFromParams || zoneFromParams
        ? moment('2024-04-01')
        : moment(new Date()),
    endDate: moment(new Date()),
  });
  const [selectedOffice, setSelectedOffice] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState({
    label: t('all_locations'),
    value: null,
  });

  const [selectedMember, setSelectedMember] = useState('');
  const [taskStatus, setTaskStatus] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [search, setSearch] = useState(false);
  const [currentSearchedTerm, setCurrentSearchedTerm] = useState('');
  const [expandedTaskId, setExpandedTaskId] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [taskIdDelete, setTaskIdDelete] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  // pagination
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [selectedPublicZone, setSelectedPublicZone] = useState('');
  const [selectedSubTask, setSelectedSubTask] = useState();
  const statusOptions = TaskStatusOptions?.map(option => ({
    ...option,
    label: t(option.key),
  }));

  const onPublicZoneChange = e => {
    setSelectedPublicZone(e);
    setSelectedLocation({
      label: t('all_locations'),
      value: null,
    });
    setSelectedSubTask({
      label: t('all_sub_tasks'),
      value: null,
    });
  };
  const onSubTaskChange = e => {
    setSelectedSubTask(e);
  };
  const selectLocation = e => {
    setCurrentPageNumber(1);
    setSearchTerm('');
    setCurrentSearchedTerm('');
    setSelectedLocation(e);
  };
  useEffect(() => {
    statusFromParams
      ? statusOptions.map(statusOption => {
          if (statusOption.key === statusFromParams) {
            setTaskStatus(statusOption);
          }
        })
      : setTaskStatus({
          label: t('all_status'),
          value: null,
        });
    zoneFromParams &&
      statusOptions.map(statusOption => {
        if (statusOption.key === statusFromParams) {
          setTaskStatus(statusOption);
        }
      });
    getTaskLocation();
    getPublicZoneList();
  }, []);

  useEffect(() => {
    if (selectedPublicZone?.value) {
      getDefaultSubTasks({
        public_zone_id: selectedPublicZone.value,
      });
    } else getDefaultSubTasks();
    if (selectedPublicZone?.value) {
      getOfficesShortList({
        public_zone_id: selectedPublicZone.value,
      });
    }
  }, [selectedPublicZone]);
  useEffect(() => {
    if (!selectedOffice) {
      zoneFromParams
        ? officesShortList.map(office => {
            if (office.name === zoneFromParams) {
              setSelectedOffice({
                label: office.name,
                value: office.id,
              });
            }
          })
        : setSelectedOffice({
            label: t('all_zones'),
            value: null,
          });
    }
  }, [officesShortList]);

  useEffect(() => {
    if (deletedCallback) {
      getSubTasks({
        startDate: moment(selectedDate.startDate).format('YYYY-MM-DD'),
        endDate: moment(selectedDate.endDate).format('YYYY-MM-DD'),
        page: currentPageNumber,
      });
    }
  }, [deletedCallback]);

  useEffect(() => {
    if (createdTaskInfo) {
      getSubTasks({
        startDate: moment(selectedDate.startDate).format('YYYY-MM-DD'),
        endDate: moment(selectedDate.endDate).format('YYYY-MM-DD'),
        page: currentPageNumber,
      });
    }
  }, [createdTaskInfo]);
  const handleSearchByName = () => {
    setCurrentSearchedTerm(searchTerm);
    setSearch(!search);
    setCurrentPageNumber(1);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setCurrentSearchedTerm('');
    setSearch(!search);
  };

  const handlePageChange = page => {
    setCurrentPageNumber(page);
  };

  const selectOffice = e => {
    setCurrentPageNumber(1);
    setSearchTerm('');
    setCurrentSearchedTerm('');
    setSelectedOffice(e);
    setSelectedLocation({
      label: t('all_locations'),
      value: null,
    });
  };

  const selectMember = person => {
    setSelectedMember(person);
    setSearchTerm('');
    setCurrentSearchedTerm('');
    setCurrentPageNumber(1);
  };

  const clearMember = () => {
    setSelectedMember('');
    setSearchTerm('');
    setCurrentSearchedTerm('');
    setCurrentPageNumber(1);
  };

  const onSearchTermChange = e => {
    const { value } = e.target;
    if (value !== '') {
      setSelectedMember('');
    }
    setSearchTerm(value);
  };

  const handleStatusChange = e => {
    setTaskStatus(e);
  };

  const handleDateChange = date => {
    setSelectedDate(date);
  };

  const toggleDeleteModal = taskId => {
    setIsOpen(!isOpen);
    if (taskId) {
      setTaskIdDelete(taskId);
    }
  };

  const handleDeleteTask = () => {
    if (taskIdDelete) {
      const payload = {
        task_id: taskIdDelete,
      };
      deleteTask(payload);
      toggleDeleteModal();
    }
  };
  const handleExportToExcel = async () => {
    const payload = {
      startDate: moment(selectedDate.startDate).format('YYYY-MM-DD'),
      endDate: moment(selectedDate.endDate).format('YYYY-MM-DD'),
      page: currentPageNumber,
    };
    if (selectedOffice && selectedOffice.value) {
      payload.office_id = selectedOffice.value;
    }
    if (selectedPublicZone && selectedPublicZone.value) {
      payload.public_zone_name = selectedPublicZone.label;
    }
    if (selectedSubTask && selectedSubTask.code) {
      payload.sub_task_code = selectedSubTask.code;
    }
    if (selectedLocation && selectedLocation.value) {
      payload.location_id = selectedLocation.value;
    }
    if (selectedMember && selectedMember.id) {
      payload.assignee_id = selectedMember.id;
    }
    if (searchTerm && searchTerm.trim()) {
      payload.task_name = searchTerm.toLowerCase().trim();
    }
    if (taskStatus && taskStatus.value && taskStatus.value !== 'all') {
      payload.status = taskStatus.value;
    }
    // Remove null or undefined properties
    const filteredPayload = Object.fromEntries(
      Object.entries(payload).filter(([_, v]) => v != null),
    );

    if (filteredPayload.startDate && filteredPayload.endDate) {
      const url = `albait/export/sub-task-report/?required_start_date=${
        filteredPayload.startDate
      }&required_end_date=${filteredPayload.endDate}${
        filteredPayload.page ? `&page=${filteredPayload.page}` : ''
      }${
        filteredPayload.task_name?.length > 0
          ? `&name=${filteredPayload.task_name}`
          : ''
      }${
        filteredPayload.office_id
          ? `&office_id=${filteredPayload.office_id}`
          : ''
      }${
        filteredPayload.public_zone_name
          ? `&public_zone_name=${filteredPayload.public_zone_name}`
          : ''
      }${
        filteredPayload.sub_task_code
          ? `&sub_task_code=${filteredPayload.sub_task_code}`
          : ''
      }${
        filteredPayload.location_id
          ? `&location_id=${filteredPayload.location_id}`
          : ''
      }${
        filteredPayload.assignee_id
          ? `&assignees=${filteredPayload.assignee_id}`
          : ''
      }${filteredPayload.status ? `&status=${filteredPayload.status}` : ''}${
        filteredPayload.approval_status
          ? `&approval_status=${filteredPayload.approval_status}`
          : ''
      }`;

      setReportLoading(true);
      try {
        const response = await exportToExcel(url, null, true);
      } catch (error) {
        console.log('🚀 ~ handleExportToExcel ~ error:', error);
      }
      setReportLoading(false);
    }
  };

  const getTasks = () => {
    const payload = {
      startDate: moment(selectedDate.startDate).format('YYYY-MM-DD'),
      endDate: moment(selectedDate.endDate).format('YYYY-MM-DD'),
      page: currentPageNumber,
    };
    if (selectedOffice && selectedOffice.value) {
      payload.office_id = selectedOffice.value;
    }
    if (selectedPublicZone && selectedPublicZone.value) {
      payload.public_zone_name = selectedPublicZone.label;
    }
    if (selectedSubTask && selectedSubTask.code) {
      payload.sub_task_code = selectedSubTask.code;
    }
    if (selectedLocation && selectedLocation.value) {
      payload.location_id = selectedLocation.value;
    }
    if (selectedMember && selectedMember.id) {
      payload.assignee_id = selectedMember.id;
    }
    if (searchTerm && searchTerm.trim()) {
      payload.task_name = searchTerm.toLowerCase().trim();
    }
    if (taskStatus && taskStatus.value && taskStatus.value !== 'all') {
      payload.status = taskStatus.value;
    }
    // Remove null or undefined properties
    const filteredPayload = Object.fromEntries(
      Object.entries(payload).filter(([_, v]) => v != null),
    );

    getSubTasks(filteredPayload);
  };
  useEffect(() => {
    getTasks();
  }, [
    currentPageNumber,
    selectedDate,
    selectedOffice,
    selectedMember,
    // searchTerm,
    search,
    taskStatus,
    selectedLocation,
    selectedPublicZone,
    selectedSubTask,
  ]);

  return (
    <div className="content">
      <DeleteTaskModal
        isOpen={isOpen}
        toggle={() => toggleDeleteModal()}
        handleDelete={handleDeleteTask}
        isLoading={deleteIsLoading}
      />
      <CommonGrid alignItem="center">
        <PageTitle>{t('all_sub_tasks')}</PageTitle>{' '}
        <CommonFlex gap="12px">
          {localStorage.getItem('user_role') !== 'employee' ? (
            <WhiteButtonWithIcon
              onClick={() => handleExportToExcel()}
              color="#19AC65"
              borderColor="#19AC65"
              hoverBorderColor="#129652"
              padding={reportLoading ? '0 45px' : '0 25px'}
            >
              {reportLoading ? (
                <ButtonTextLoader color="#19AC65" loadingText=" " size={6} />
              ) : (
                <>
                  <ButtonIcon src={excelIcon} size="26px" />
                  {t('export_report')}
                </>
              )}
            </WhiteButtonWithIcon>
          ) : null}
        </CommonFlex>
      </CommonGrid>
      <HeaderContainer columns="1fr">
        <SubTaskFilter
          getOrganizationMembersList={getOrganizationMembersList}
          organizationMembersList={orgMembersList}
          officesShortList={officesShortList}
          getOfficesShortList={getOfficesShortList}
          selectedOffice={selectedOffice}
          selectOffice={selectOffice}
          locationList={locationsList}
          selectedLocation={selectedLocation}
          selectLocation={selectLocation}
          selectedMember={selectedMember}
          selectMember={selectMember}
          clearMember={clearMember}
          searchTerm={searchTerm}
          onSearchTermChange={onSearchTermChange}
          taskStatus={taskStatus}
          selectedDate={selectedDate}
          startDate={selectedDate.startDate}
          endDate={selectedDate.endDate}
          handleDateChange={handleDateChange}
          handleStatusChange={handleStatusChange}
          handleSearchByName={handleSearchByName}
          handleClearSearch={handleClearSearch}
          selectedPublicZone={selectedPublicZone}
          onPublicZoneChange={onPublicZoneChange}
          publicZoneList={publicZoneList}
          getTaskLocation={getTaskLocation}
          defaultSubTasks={defaultSubTasks}
          selectedSubTask={selectedSubTask}
          onSubTaskChange={onSubTaskChange}
        />
      </HeaderContainer>

      {totalTasksCount && totalTasksCount > 0 && pageSize ? (
        <TopPagination
          itemName={t('Tasks')}
          totalCount={totalTasksCount}
          currentPage={currentPageNumber}
          onPageChange={page => handlePageChange(page)}
          pageSize={pageSize}
        />
      ) : null}

      <SubTasksList
        selectedOrganization={selectedOrganization}
        loadingTaskAssigneeId={loadingTaskAssigneeId}
        expandedTaskId={expandedTaskId}
        setExpandedTaskId={setExpandedTaskId}
        toggleDeleteModal={toggleDeleteModal}
        history={history}
        subTasksList={subTasksList}
        isLoading={isLoading}
        createTask={createTask}
      />

      {totalTasksCount && totalTasksCount > 0 && pageSize ? (
        <BottomPagination
          totalCount={totalTasksCount}
          currentPage={currentPageNumber}
          onPageChange={page => handlePageChange(page)}
          pageSize={pageSize}
        />
      ) : null}
    </div>
  );
};

export default SubTasks;
